import { useDocumentViewerContext } from '../../../contexts/DocumentViewerContextProvider';
import { NEW_BLANK_REPORT_JSON } from '../../../consts/reportConstants';
import createViewport from '../utils/createViewport';
import { BLANK_REPORT_ID } from '../../../consts/consts';
import useCheckAccess from '../../../hooks/useCheckAccess';
import useBestMatchTemplateForStudy from './useBestMatchTemplateForStudy';
import { useDocumentViewerPermissionsContext } from '../../../contexts/DocumentViewerPermissionsContextProvider';
import useReportAccessIndicator from '../../../hooks/useReportAccessIndicator';
import useCreateMDAiReportLink from '../../../hooks/useCreateMDAiReportLink';
import { useBooleanFlagValue } from '@rs-core/hooks/useFlags';
import { logError, logInfo } from '@rs-core/utils/logger';
import { useGlobalStore } from '@rs-core/store/globalStore';
import { MD_AI_REPORT_CREATION_PROGRESS } from '@rs-core/store/slices/aiReportingIntegrationSlice';

const useAddBlankReport = () => {
	const galaxyDvEnableMdAiIntegration = useBooleanFlagValue('galaxy-dv-enable-md-ai-integration');

	const { createMdAiReport } = useCreateMDAiReportLink();
	const { setBlankReport, blankReport, setViewports, setAppliedPageSetup } = useDocumentViewerContext();
	const { getBestMatchTemplate } = useBestMatchTemplateForStudy();
	const { PRELIMINARY_REPORT_PERMISSION } = useDocumentViewerPermissionsContext();
	const mdAiReportCreationProgress = useGlobalStore(
		state => state.aiReportingIntegration.mdAi.mdAiReportCreationProgress
	);
	const reportCreatePermission = PRELIMINARY_REPORT_PERMISSION?.CREATE;

	const BLANK_REPORT_JSON = NEW_BLANK_REPORT_JSON;

	const { hasFullAccess } = useCheckAccess();

	const { acquireLock } = useReportAccessIndicator();

	const addBlankReport = async force => {
		if (!hasFullAccess || !reportCreatePermission) return;
		/* prevent creation more than 1 blank report */
		if (blankReport && !force) {
			return;
		}
		if (
			galaxyDvEnableMdAiIntegration &&
			mdAiReportCreationProgress === MD_AI_REPORT_CREATION_PROGRESS.NOT_STARTED
		) {
			logInfo('useAddBlankReport', 'Creating MD.ai report link');
			await createMdAiReport();
			logInfo('useAddBlankReport', 'MD.ai report link created');
		}

		try {
			// Continue with main report creation flow
			await acquireLock(BLANK_REPORT_ID);
			await getBestMatchTemplate(true);

			setBlankReport({ ...BLANK_REPORT_JSON });
			const blankViewport = createViewport({
				documentId: BLANK_REPORT_ID,
				documentResourceType: NEW_BLANK_REPORT_JSON.resourceType,
			});
			setViewports([blankViewport]);
			setAppliedPageSetup({});
			//document.querySelector('.study-navigator-content') is the Study-NavigatorScrollableContent
			//container.querySelectorAll('.study-category-title')[0] is a Preliminary report text in current study category
			const container = document?.querySelector('.study-navigator-content');
			const element = container?.querySelectorAll('.study-category-title')?.[0];

			//Scroll to Preliminary report text. 58px is the height of Study-Header which will came on top of Preliminary Text
			if (container && element) {
				const scrollTop = element.offsetTop - 58;
				container.scrollTo({
					top: scrollTop,
					behavior: 'smooth',
				});
			}
		} catch (error) {
			logError('useAddBlankReport', 'Failed to create blank report:', error);
		}
	};

	return {
		addBlankReport,
	};
};

export default useAddBlankReport;
